/**
 * MasterWEB Market R8.31 — selective component loader.
 *
 * Loads Horizon feature modules only when matching markup exists. A lightweight
 * MutationObserver also covers Shopify Section Rendering / theme-editor DOM
 * updates so dynamically inserted components still upgrade correctly.
 */

const registry = [
  ['overflow-list', '@theme/overflow-list'],
  ['dialog-component', '@theme/dialog'],
  ['quick-add-component, quick-add-dialog', '@theme/quick-add'],
  ['mw-product-actions', '@theme/mw-product-actions'],
  ['variant-picker, swatches-variant-picker-component', '@theme/variant-picker'],
  ['product-card, product-card-link', '@theme/product-card'],
  ['product-form-component, add-to-cart-component', '@theme/product-form'],
  ['fly-to-cart', '@theme/fly-to-cart'],
  ['accordion-custom', '@theme/accordion-custom'],
  ['deferred-media, product-model', '@theme/media'],
  ['product-price', '@theme/product-price'],
  ['product-sku-component', '@theme/product-sku'],
  ['product-inventory', '@theme/product-inventory'],
  ['show-more-component', '@theme/show-more'],
  ['slideshow-component', '@theme/slideshow'],
  ['layered-slideshow-component', '@theme/layered-slideshow'],
  ['anchored-popover-component', '@theme/anchored-popover'],
  ['floating-panel-component', '@theme/floating-panel'],
  ['video-background-component', '@theme/video-background'],
  ['quantity-selector-component', '@theme/component-quantity-selector'],
  ['media-gallery', '@theme/media-gallery'],
  ['rte-formatter', '@theme/rte-formatter'],
  ['volume-pricing', '@theme/volume-pricing'],
  ['price-per-item', '@theme/price-per-item'],
  ['volume-pricing-info', '@theme/volume-pricing-info'],
  ['cart-discount-component', '@theme/cart-discount'],
  ['dropdown-localization-component, drawer-localization-component, localization-form-component', '@theme/localization'],
  ['sticky-add-to-cart', '@theme/sticky-add-to-cart'],
];

const requested = new Set();

const requestModule = (specifier) => {
  if (requested.has(specifier)) return;
  requested.add(specifier);
  import(specifier).catch((error) => {
    requested.delete(specifier);
    console.error(`[MasterWEB] Failed to load ${specifier}`, error);
  });
};

const scanDocument = () => {
  for (const [selector, specifier] of registry) {
    if (!requested.has(specifier) && document.querySelector(selector)) requestModule(specifier);
  }
};

const scanNode = (node) => {
  if (!(node instanceof Element)) return;
  for (const [selector, specifier] of registry) {
    if (requested.has(specifier)) continue;
    if (node.matches(selector) || node.querySelector(selector)) requestModule(specifier);
  }
};

scanDocument();

const root = document.body;
if (root && requested.size < registry.length) {
  const pendingRoots = new Set();
  let frame = 0;

  const flush = () => {
    frame = 0;
    for (const node of pendingRoots) {
      if (node.isConnected) scanNode(node);
    }
    pendingRoots.clear();
    if (requested.size === registry.length) observer.disconnect();
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) pendingRoots.add(node);
      }
    }
    // Shopify can inject many nodes in one render. Scan once on the next frame
    // instead of running every selector against every mutation synchronously.
    if (!frame && pendingRoots.size) frame = requestAnimationFrame(flush);
  });

  observer.observe(root, { childList: true, subtree: true });

  // Avoid retaining the observer across page lifecycle transitions/BFCache.
  window.addEventListener('pagehide', () => {
    if (frame) cancelAnimationFrame(frame);
    pendingRoots.clear();
    observer.disconnect();
  }, { once: true });
}
